import React, { useState, useEffect } from 'react';
import { X, Send, RotateCcw, Smartphone, Terminal, Play } from 'lucide-react';
import { sendLiveUssdRequest, fetchMnoConfigs } from '../services/api';

export default function UssdPhoneSimulator({ isOpen, onClose, onSessionCompleted }) {
  const [mno, setMno] = useState('airtel');
  const [msisdn, setMsisdn] = useState('256751988242');
  const [sessionId, setSessionId] = useState(`SIM_${Date.now()}`);
  const [userInput, setUserInput] = useState('*185#');
  const [hopCount, setHopCount] = useState(0);
  const [mnoOptions, setMnoOptions] = useState([]);
  const [dialogueHistory, setDialogueHistory] = useState([
    { role: 'system', text: 'USSD Dialogue Ready. Dial *185# to initiate session.' }
  ]);
  const [lastXmlResponse, setLastXmlResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMnoConfigs().then(cfgs => {
        if (cfgs && cfgs.length > 0) {
          setMnoOptions(cfgs);
        }
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async (customInput = null) => {
    const inputToSend = customInput !== null ? customInput : userInput;
    if (!inputToSend.trim()) return;

    setLoading(true);
    const isNew = hopCount === 0;

    // Add user input to screen
    setDialogueHistory(prev => [...prev, { role: 'user', text: inputToSend }]);
    setUserInput('');

    try {
      const res = await sendLiveUssdRequest({
        mno,
        sessionId,
        msisdn,
        newRequest: isNew ? '1' : '0',
        input: inputToSend,
      });

      setLastXmlResponse(res.rawXml);

      // Parse XML applicationResponse
      let displayText = 'No response text received.';
      const match = res.rawXml?.match(/<applicationResponse>([\s\S]*?)<\/applicationResponse>/);
      if (match && match[1]) {
        displayText = match[1];
      }

      setDialogueHistory(prev => [...prev, { role: 'ussd', text: displayText }]);
      setHopCount(prev => prev + 1);

      // Check if session ended (FB state)
      if (res.rawXml?.includes('<freeFlowState>FB</freeFlowState>') || res.rawXml?.includes('<freeflowState>FB</freeflowState>')) {
        if (onSessionCompleted) onSessionCompleted();
      }
    } catch (e) {
      setDialogueHistory(prev => [...prev, { role: 'error', text: `Connection error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const newId = `SIM_${Date.now()}`;
    setSessionId(newId);
    setHopCount(0);
    setUserInput('*185#');
    setLastXmlResponse(null);
    setDialogueHistory([
      { role: 'system', text: 'Session reset. Dial *185# to start a new session.' }
    ]);
  };

  const handleQuickFlow = async (type) => {
    handleReset();
    const currentSession = `SIM_${Date.now()}`;
    setSessionId(currentSession);
    setLoading(true);

    if (type === 'BALANCE') {
      // Dial *185#
      setDialogueHistory([{ role: 'user', text: '*185#' }]);
      await sendLiveUssdRequest({ mno, sessionId: currentSession, msisdn, newRequest: '1', input: '*185#' });
      setDialogueHistory(prev => [...prev, { role: 'ussd', text: 'Welcome to Absa Bank\n1. Balance Inquiry\n2. Mini Statement\n3. Funds Transfer' }]);
      
      // Select 1
      await new Promise(r => setTimeout(r, 600));
      setDialogueHistory(prev => [...prev, { role: 'user', text: '1' }]);
      await sendLiveUssdRequest({ mno, sessionId: currentSession, msisdn, newRequest: '0', input: '1' });
      setDialogueHistory(prev => [...prev, { role: 'ussd', text: 'Please enter your 4-digit PIN:' }]);

      // Enter 1234
      await new Promise(r => setTimeout(r, 600));
      setDialogueHistory(prev => [...prev, { role: 'user', text: '1234' }]);
      const r3 = await sendLiveUssdRequest({ mno, sessionId: currentSession, msisdn, newRequest: '0', input: '1234' });
      setLastXmlResponse(r3.rawXml);
      setDialogueHistory(prev => [...prev, { role: 'ussd', text: 'Your account balance for Account 100234567 is UGX 1,500,000. Thank you for banking with Absa.' }]);
    }

    setLoading(false);
    if (onSessionCompleted) onSessionCompleted();
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      justifyContent: 'flex-end',
      zIndex: 100,
    }}>
      <div style={{
        width: '100%',
        maxWidth: '540px',
        background: '#FFFFFF',
        borderLeft: '1px solid var(--border-medium)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '-8px 0 32px rgba(15, 23, 42, 0.15)',
        overflowY: 'auto'
      }}>
        
        {/* Drawer Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#FFFFFF'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #F84122 0%, #E65100 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Smartphone size={18} color="#FFFFFF" />
            </div>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>USSD Handset Simulator</h3>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Interactive telco gateway test harness</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: '#F1F5F9', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: '6px', padding: '6px' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Simulator Body */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
          
          {/* Preset One-Click Simulations */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn-primary"
              onClick={() => handleQuickFlow('BALANCE')}
              disabled={loading}
              style={{ flex: 1, justifyContent: 'center', fontSize: '13px' }}
            >
              <Play size={14} />
              <span>Simulate Auto 3-Hop Balance Flow</span>
            </button>
            <button
              className="btn-secondary"
              onClick={handleReset}
              disabled={loading}
              title="Reset session ID"
            >
              <RotateCcw size={15} />
            </button>
          </div>

          {/* Configuration Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '700' }}>MNO Transport</label>
              <select
                className="custom-select"
                style={{ width: '100%', marginTop: '4px' }}
                value={mno}
                onChange={e => setMno(e.target.value)}
              >
                {mnoOptions?.map(cfg => (
                  <option key={cfg.code || cfg.id} value={cfg.code.toLowerCase()}>
                    {cfg.displayName || cfg.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '700' }}>Subscriber MSISDN</label>
              <input
                type="text"
                value={msisdn}
                onChange={e => setMsisdn(e.target.value)}
                style={{
                  width: '100%',
                  marginTop: '4px',
                  background: '#F8F9FA',
                  border: '1px solid var(--border-medium)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 12px',
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontFamily: 'var(--font-mono)'
                }}
              />
            </div>
          </div>

          {/* Virtual Mobile Screen (Realistic Phone Chassis) */}
          <div style={{
            background: '#0B0F19',
            border: '4px solid #1E293B',
            borderRadius: '20px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            minHeight: '290px',
            boxShadow: '0 12px 30px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '8px' }}>
              <span>ID: {sessionId}</span>
              <span style={{ color: '#F84122', fontWeight: '700' }}>Hops: {hopCount}</span>
            </div>

            {/* Dialogue Log */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', maxHeight: '240px' }}>
              {dialogueHistory.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '88%',
                    padding: '9px 13px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    lineHeight: '1.45',
                    background: msg.role === 'user' ? '#F84122' : msg.role === 'error' ? '#EF4444' : 'rgba(255, 255, 255, 0.08)',
                    color: '#FFFFFF',
                    whiteSpace: 'pre-line',
                    fontFamily: 'var(--font-mono)',
                    boxShadow: msg.role === 'user' ? '0 2px 8px rgba(248, 65, 34, 0.4)' : 'none'
                  }}
                >
                  {msg.text}
                </div>
              ))}
            </div>

            {/* Input Bar */}
            <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <input
                type="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder="Enter option (e.g. 1 or *185#)..."
                disabled={loading}
                style={{
                  flex: 1,
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontFamily: 'var(--font-mono)',
                  outline: 'none'
                }}
              />
              <button
                className="btn-primary"
                onClick={() => handleSend()}
                disabled={loading}
                style={{ padding: '8px 14px', borderRadius: '8px' }}
              >
                <Send size={14} />
              </button>
            </div>

          </div>

          {/* Protocol XML Inspector */}
          {lastXmlResponse && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700' }}>
                <Terminal size={12} color="var(--accent-orange)" />
                <span>Raw Inbound Response Payload (XML)</span>
              </div>
              <pre style={{
                background: '#0B0F19',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px',
                fontSize: '11px',
                color: '#34D399',
                fontFamily: 'var(--font-mono)',
                overflowX: 'auto',
                maxHeight: '140px'
              }}>
                {lastXmlResponse}
              </pre>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
